import { useEffect } from 'react';
import { useWasserijItemDrawerStore } from '../../stores/wasserij-item-drawer-store';
import { WasserijItemDetailView } from './WasserijItemDetailView';

/**
 * Overlay-drawer voor Wasserij_item__c detail. Globaal gemount in AppLayout,
 * zodat elke page `useWasserijItemDrawerStore.open(id)` kan aanroepen.
 * Met een lijst-context (`open(id, listIds)`) toont de drawer vorige/volgende
 * zodat een lijst item-voor-item verwerkt kan worden (ook via pijltjestoetsen).
 */
export function WasserijItemDrawerPanel() {
  const openItemId = useWasserijItemDrawerStore((s) => s.openItemId);
  const listIds = useWasserijItemDrawerStore((s) => s.listIds);
  const open = useWasserijItemDrawerStore((s) => s.open);
  const close = useWasserijItemDrawerStore((s) => s.close);

  // Esc-to-close + pijltjes voor lijst-bladeren. Capture-phase zodat het boven
  // page-level handlers komt. Negeer toetsen in inputs zodat typen niet sluit
  // en pijltjes in een select blijven werken.
  useEffect(() => {
    if (!openItemId) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) {
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        close();
        return;
      }
      const { openItemId: current, listIds: list } = useWasserijItemDrawerStore.getState();
      if (!current || !list) return;
      const idx = list.indexOf(current);
      if (idx < 0) return;
      const target = e.key === 'ArrowLeft' ? list[idx - 1] : list[idx + 1];
      if (!target) return;
      e.preventDefault();
      e.stopPropagation();
      open(target);
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [openItemId, close, open]);

  if (!openItemId) return null;

  const idx = listIds ? listIds.indexOf(openItemId) : -1;
  const showNav = idx >= 0 && listIds && listIds.length > 1;

  return (
    <>
      <div
        className="fixed inset-0 bg-slate-900/30 z-[65]"
        onClick={close}
        aria-hidden
      />
      <aside
        className="fixed top-0 right-0 h-full w-full max-w-xl bg-white shadow-2xl z-[70] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label="Wasserij-item detail"
      >
        {showNav && listIds && (
          <div className="flex items-center justify-between bg-slate-50 border-b border-slate-200 px-4 py-1.5 flex-shrink-0">
            <button
              type="button"
              onClick={() => {
                const target = listIds[idx - 1];
                if (target) open(target);
              }}
              disabled={idx <= 0}
              className="text-xs text-slate-600 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              &larr; Vorige
            </button>
            <span className="text-xs text-slate-500" title="Bladeren kan ook met de pijltjestoetsen">
              Kleed {idx + 1} van {listIds.length}
            </span>
            <button
              type="button"
              onClick={() => {
                const target = listIds[idx + 1];
                if (target) open(target);
              }}
              disabled={idx >= listIds.length - 1}
              className="text-xs text-slate-600 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Volgende &rarr;
            </button>
          </div>
        )}
        <WasserijItemDetailView
          key={openItemId}
          itemId={openItemId}
          onClose={close}
        />
      </aside>
    </>
  );
}
